import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circle-react-uikit/ModalV2";
import { useCreatePostModalInvalidations } from "@circle-react/components/Spaces/ImageSpace/hooks/useCreatePostModalInvalidations";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { useNavigate } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";
import { useCurrentPostHelpers } from "@/react/components/PostsV3/useCurrentPostHelpers";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { useImagePostMutation } from "@/react/components/Spaces/ImageSpace/ImagePostModal/CreatePost/hooks/useImagePostMutation";
import { afterSaveRedirectPath } from "@/react/helpers/postHelpers";
import type { ImagePost } from "@/react/types";

import { Actions } from "./Actions";
import { Carousel } from "./Carousel";
import { defaultValues } from "./helpers";
import { ImagePostForm } from "./ImagePostForm";

interface CreatePostProps {
  onClose: (skipDirtyValidation?: boolean) => void;
  onPostChange: (post: ImagePost) => void;
  shouldSkipRedirectAfterSubmit: boolean;
}

export const CreatePost = ({
  onClose,
  onPostChange,
  shouldSkipRedirectAfterSubmit,
}: CreatePostProps) => {
  const { post } = useCurrentPostHelpers();
  const { data: space } = useCurrentSpaceContext();
  const invalidateQueries = useCreatePostModalInvalidations();

  const navigate = useNavigate();
  const onSuccess = async (response: any) => {
    onPostChange(post);
    await invalidateQueries(response.id);
    onClose(true);
    if (!shouldSkipRedirectAfterSubmit) {
      void navigate({ href: afterSaveRedirectPath({ post: response }) });
    }
  };
  const { onSubmit } = useImagePostMutation({ space, post, onSuccess });
  return (
    <Modal isOpen onClose={onClose} contentPortalId="create-post-modal-portal">
      <Form
        onSubmit={onSubmit}
        defaultValues={defaultValues(post)}
        formClassName="fixed inset-0 z-[1060]"
      >
        <Modal.Overlay />
        <Modal.Content
          size="full"
          className="sm:!max-h-[836px] sm:!max-w-[1312px]"
        >
          <Modal.Header className="z-10 md:!py-4">
            <Typography.TitleSm weight="font-semibold">
              {post.id
                ? t("image_space.post_modal.steps.create_post.edit_title")
                : t("image_space.post_modal.steps.create_post.create_title")}
            </Typography.TitleSm>
            <Modal.HeaderActions className="!gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => onClose(false)}
              >
                {t("cancel")}
              </Button>
              <Actions post={post} />
            </Modal.HeaderActions>
          </Modal.Header>
          <Modal.Body className="flex flex-wrap content-start !p-0 md:border-t md:border-primary">
            <Carousel />
            <ImagePostForm post={post} space={space} />
          </Modal.Body>
        </Modal.Content>
      </Form>
    </Modal>
  );
};
